import moment from 'moment';

import { random as lrandom } from 'lodash';
import { IslandType, SkillType, TableExperienceType } from '../lib/types';

export function arrayContains(haystack: number[][], needle: number[]): number {
  for (let i = 0; i < haystack.length; i += 1) {
    if (needle.length === haystack[i].length) {
      const current = haystack[i];
      let j;
      for (j = 0; j < needle.length && needle[j] === current[j]; j += 1);
      if (j === needle.length) return i;
    }
  }
  return -1;
}

export function mapKeyObjToObj(obj: Record<string, any>): Record<string, any> {
  const id = Object.keys(obj)[0];
  return { id, ...obj[id] };
}

export function mapObjToArray(obj: Record<string, any>): any[] {
  return Object.keys(obj).map((key) => ({
    id: key,
    ...obj[key],
  }));
}

export function compareObj(obj1: any, obj2: any) {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

export function formatNumber(value: number): number {
  return parseFloat(value.toFixed(2));
}

export function timeout(ms: number): Promise<number> {
  return new Promise((resolve: Function) => setTimeout(resolve, ms));
}

export function getMapMargin(
  x: number,
  y: number,
  mapDimensions: { width: number; height: number },
  islandDimensions: { width: number; height: number },
): { left: number; top: number } {
  // 5, 3
  // [[],[],[],  [],[]]
  // [[],[],[], [],[]]
  // [[], [],[],[], []]
  // [[],[], [],[],[]]
  // [[],[],  [],[],[]]

  // 1 - 3 / 2 = -0.5
  // 2 - 3 / 2 = 0.5
  // 3 - 3 / 2 = 1.5 | 3.5 < 3 false
  // 4 - 3 / 2 = 2.5 | 2.5 < 3 true
  // 5 - 3 / 2 = 3.5 | 1.5 < 3 true

  // 6, 3
  // [[],[],[],  [],[],[]]
  // [[],[],[], [],[],[]]
  // [[], [],[],[], [],[]]
  // [[],[], [],[],[], []]
  // [[],[],[], [],[],[]]
  // [[],[],[], [],[],[]]

  // 1 - 3 / 2 = -0.5
  // 2 - 3 / 2 = 0.5
  // 3 - 3 / 2 = 1.5 | 4.5 < 3 false
  // 4 - 3 / 2 = 2.5 | 3.5 < 3 false
  // 5 - 3 / 2 = 3.5 | 2.5 < 3 true
  // 6 - 3 / 2 = 4.5 | 1.5 < 3 true

  // 6, 4
  // [[],[],[],[]  [],[]]
  // [[],[],[],[], [],[]]
  // [[], [],[],[],[], []]
  // [[],[], [],[],[],[]]
  // [[],[], [],[],[],[]]
  // [[],[], [],[],[],[]]

  // 1 - 4 / 2 = -1
  // 2 - 4 / 2 = 0
  // 3 - 4 / 2 = 1 | 5 < 4 false
  // 4 - 4 / 2 = 2 | 4 < 4 false
  // 5 - 4 / 2 = 3 | 3 < 4 true
  // 6 - 4 / 2 = 4 | 2 < 4 true

  const widthDivided = mapDimensions.width / 2;
  const heightDivided = mapDimensions.height / 2;
  const mapMargin = {
    left: x - widthDivided,
    top: y - heightDivided,
  };

  if (Math.floor(mapMargin.left) <= 0) {
    mapMargin.left = 0;
  } else if (islandDimensions.width - mapMargin.left <= mapDimensions.width) {
    mapMargin.left = islandDimensions.width - mapDimensions.width;
  }

  if (Math.floor(mapMargin.top) <= 0) {
    mapMargin.top = 0;
  } else if (islandDimensions.height - mapMargin.top <= mapDimensions.height) {
    mapMargin.top = islandDimensions.height - mapDimensions.height;
  }

  return mapMargin;
}

export function getEnabledCoords(disabledCoords: number[][], startCoords: number[]) {
  const color = '*';
  const replaceColor = '0';

  const field: string[][] = Array.from({ length: 38 }, () => []);
  field.forEach((item, index) => {
    field[index] = Array.from({ length: 38 }, () => '0');
  });

  field.forEach((item, x) => {
    field.forEach((iitem, y) => {
      if (arrayContains(disabledCoords, [x, y]) !== -1) field[x][y] = 'X';
    });
  });

  const result: number[][] = [];
  const setted: string[] = [];
  const pixelStack = [startCoords];

  function checkPixel(x: number, y: number) {
    try {
      return field[x][y] === color || field[x][y] === replaceColor;
    } catch (e) {
      return false;
    }
  }

  while (pixelStack.length) {
    const currentPixel = pixelStack.pop() as number[];
    const x = currentPixel[0];
    const y = currentPixel[1];

    /* eslint-disable no-continue */
    if (setted.includes(`${x}, ${y}`)) continue;

    field[x][y] = color;
    result.push([x, y]);

    setted.push(`${x}, ${y}`);

    if (checkPixel(x - 1, y - 1)) pixelStack.push([x - 1, y - 1]);
    if (checkPixel(x - 1, y)) pixelStack.push([x - 1, y]);
    if (checkPixel(x - 1, y + 1)) pixelStack.push([x - 1, y + 1]);

    if (checkPixel(x, y - 1)) pixelStack.push([x, y - 1]);
    if (checkPixel(x, y + 1)) pixelStack.push([x, y + 1]);

    if (checkPixel(x + 1, y - 1)) pixelStack.push([x + 1, y - 1]);
    if (checkPixel(x + 1, y)) pixelStack.push([x + 1, y]);
    if (checkPixel(x + 1, y + 1)) pixelStack.push([x + 1, y + 1]);
  }

  return result;
}

export function getByValue<T>(items: T[], value: any, key: string = 'id'): T {
  return items.find((item: any) => item[key] === value) as T;
}

export function getIsland(islands: IslandType[], id: string): IslandType {
  return getByValue(islands, id);
}

export function getSkill(skills: SkillType[], id: string): SkillType {
  return getByValue(skills, id);
}

export function getItemsToLevel(level: number, tableExperience: TableExperienceType[]) {
  const toLevelTableExperience = tableExperience.filter((item) => item.level <= level);

  let numberOfParameters = 0;
  let numberOfAbilities = 0;
  let numberOfSkills = 0;
  toLevelTableExperience.forEach((item) => {
    numberOfParameters += item.numberOfParameters;
    numberOfAbilities += item.numberOfAbilities;
    numberOfSkills += item.numberOfSkills;
  });

  return { numberOfParameters, numberOfAbilities, numberOfSkills };
}

export function getTableExperience(
  tableExperience: TableExperienceType[],
  level: number,
): TableExperienceType {
  return getByValue(tableExperience, level, 'level');
}

export function getDuration(endDate: number, method: string = 'asMilliseconds') {
  const duration = Math.ceil((moment.duration(moment(endDate).diff(moment())) as any)[method]());
  return duration < 0 ? 0 : duration;
}

export function random(...args: any) {
  return lrandom(...args);
}

export function getUtcDayKey(input: number | Date): string {
  const d = typeof input === 'number' ? new Date(input) : input;
  return d.toISOString().slice(0, 10);
}
